import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video, Phone, ArrowLeft, Calendar, Clock, ChevronLeft, ChevronRight, Wallet, CreditCard } from 'lucide-react';
import { openRazorpayCheckout } from '../utils/razorpay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookingPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [expert, setExpert] = useState(null);
  const [callType, setCallType] = useState('video');
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [briefing, setBriefing] = useState('');
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [payMethod, setPayMethod] = useState('wallet');
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchExpert(); fetchWallet(); }, [userId]);

  const fetchExpert = async () => {
    try {
      const res = await fetch(`${API_URL}/api/profile/id/${userId}`, { headers });
      if (res.ok) setExpert(await res.json());
      else navigate('/app');
    } catch { navigate('/app'); }
    setLoading(false);
  };

  const fetchWallet = async () => {
    try {
      const res = await fetch(`${API_URL}/api/wallet`, { headers });
      if (res.ok) { const d = await res.json(); setWalletBalance(d.balance || 0); }
    } catch {}
  };

  const fetchSlots = async (date) => {
    setSelectedDate(date);
    setSelectedSlot('');
    setSlotsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/calendar/open-slots/${userId}?date=${date}&duration=30`, { headers });
      if (res.ok) { const d = await res.json(); setSlots(d.slots || []); }
    } catch {}
    setSlotsLoading(false);
  };

  const getPrice = () => {
    if (!expert) return 0;
    return callType === 'video' ? (expert.price_video_call || 50) : (expert.price_voice_call || 25);
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedSlot) return;
    setProcessing(true);
    const price = getPrice();

    if (payMethod === 'wallet') {
      // Pay from wallet + schedule
      try {
        const payRes = await fetch(`${API_URL}/api/wallet/pay`, {
          method: 'POST', headers,
          body: JSON.stringify({ expert_id: userId, payment_type: callType === 'video' ? 'video_call' : 'voice_call', message: briefing }),
        });
        if (payRes.ok) {
          // Also schedule in calendar
          await fetch(`${API_URL}/api/calendar/schedule`, {
            method: 'POST', headers,
            body: JSON.stringify({ expert_id: userId, date: selectedDate, time_slot: selectedSlot, call_type: callType, briefing }),
          });
          navigate('/app?tab=chat&payment=success');
          return;
        } else {
          const err = await payRes.json();
          alert(err.detail || 'Payment failed');
        }
      } catch {}
    } else {
      // UPI / Card / NetBanking via Razorpay
      try {
        const res = await fetch(`${API_URL}/api/payments/checkout`, {
          method: 'POST', headers,
          body: JSON.stringify({ expert_id: userId, payment_type: callType === 'video' ? 'video_call' : 'voice_call' }),
        });
        if (res.ok) {
          const d = await res.json();
          // Schedule first
          await fetch(`${API_URL}/api/calendar/schedule`, {
            method: 'POST', headers,
            body: JSON.stringify({ expert_id: userId, date: selectedDate, time_slot: selectedSlot, call_type: callType, briefing }),
          });
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const rzpResponse = await openRazorpayCheckout({
            order_id: d.order_id, amount: d.amount, currency: d.currency,
            key_id: d.key_id, name: 'Connect',
            description: `${d.payment_type?.replace('_', ' ')} with ${d.expert_name}`,
            prefill: { name: user?.name, email: user?.email },
          });
          const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
            method: 'POST', headers,
            body: JSON.stringify({ ...rzpResponse, txn_id: d.txn_id }),
          });
          if (verifyRes.ok) {
            navigate('/app?tab=chat&payment=success');
            return;
          }
        }
      } catch (e) {
        if (e.message !== 'Payment cancelled') alert(e.message || 'Payment failed');
      }
    }
    setProcessing(false);
  };

  // Generate next 14 days
  const dates = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return { date: d.toISOString().split('T')[0], day: d.toLocaleDateString([], { weekday: 'short' }), num: d.getDate(), month: d.toLocaleDateString([], { month: 'short' }) };
  });

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;
  if (!expert) return null;

  const price = getPrice();
  const canWalletPay = walletBalance >= price;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="booking-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/90 backdrop-blur-sm border-b border-zinc-800 px-4 py-3 flex items-center space-x-3">
        <button onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="font-bold text-lg">Book a Session</h1>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Expert Info */}
        <div className="flex items-center space-x-3 bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="expert-info">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-orange-400 p-[2px] shrink-0">
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
              {expert.picture ? <img src={expert.picture} alt="" className="w-full h-full object-cover" /> :
                <span className="text-lg font-bold">{expert.name?.[0]}</span>}
            </div>
          </div>
          <div>
            <p className="font-bold text-white">{expert.name}</p>
            <p className="text-xs text-zinc-500">{expert.headline || expert.expertise_tree?.[0]?.industry || 'Expert'}</p>
          </div>
        </div>

        {/* Call Type */}
        <div>
          <label className="text-xs text-zinc-400 font-medium mb-2 block">Call Type</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'video', label: 'Video Call', icon: Video, price: expert.price_video_call || 50, color: 'from-blue-600 to-blue-500' },
              { id: 'voice', label: 'Voice Call', icon: Phone, price: expert.price_voice_call || 25, color: 'from-green-600 to-green-500' },
            ].map(t => (
              <button key={t.id} onClick={() => setCallType(t.id)}
                className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all ${callType === t.id ? 'border-white bg-zinc-900' : 'border-zinc-800 bg-zinc-950 hover:border-zinc-700'}`}
                data-testid={`type-${t.id}`}>
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center mb-2`}>
                  <t.icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-sm font-bold">{t.label}</p>
                <p className="text-lg font-black text-white mt-1">₹{t.price}</p>
                <p className="text-[10px] text-zinc-500">30 minutes</p>
              </button>
            ))}
          </div>
        </div>

        {/* Date Picker */}
        <div>
          <label className="text-xs text-zinc-400 font-medium mb-2 block">Select Date</label>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {dates.map(d => (
              <button key={d.date} onClick={() => fetchSlots(d.date)}
                className={`flex flex-col items-center min-w-[60px] py-3 px-2 rounded-xl border transition-all shrink-0 ${selectedDate === d.date ? 'border-white bg-zinc-800' : 'border-zinc-800 hover:border-zinc-700'}`}
                data-testid={`date-${d.date}`}>
                <span className="text-[10px] text-zinc-500">{d.day}</span>
                <span className="text-lg font-bold">{d.num}</span>
                <span className="text-[10px] text-zinc-600">{d.month}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Time Slots */}
        {selectedDate && (
          <div>
            <label className="text-xs text-zinc-400 font-medium mb-2 block">Available Times</label>
            {slotsLoading ? (
              <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>
            ) : slots.filter(s => s.available).length === 0 ? (
              <p className="text-sm text-zinc-600 text-center py-6">No slots available on this day</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {slots.filter(s => s.available).map(s => (
                  <button key={s.time} onClick={() => setSelectedSlot(s.time)}
                    className={`py-2.5 rounded-xl text-sm font-medium border transition-all ${selectedSlot === s.time ? 'border-white bg-zinc-800 text-white' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}
                    data-testid={`slot-${s.time}`}>
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Briefing */}
        <div>
          <label className="text-xs text-zinc-400 font-medium mb-2 block">Pre-call note (optional)</label>
          <textarea value={briefing} onChange={e => setBriefing(e.target.value)}
            placeholder="What would you like to discuss?"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-zinc-600 resize-none"
            rows={3} data-testid="briefing-input" />
        </div>

        {/* Payment Method */}
        <div>
          <label className="text-xs text-zinc-400 font-medium mb-2 block">Payment Method</label>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setPayMethod('wallet')}
              className={`flex items-center gap-2.5 p-3.5 rounded-xl border-2 transition-all ${payMethod === 'wallet' ? 'border-emerald-500 bg-emerald-500/5' : 'border-zinc-800 hover:border-zinc-700'}`}
              data-testid="pay-wallet">
              <Wallet className="w-5 h-5 text-emerald-400" />
              <div className="text-left">
                <p className="text-sm font-bold">Wallet</p>
                <p className={`text-[10px] ${canWalletPay ? 'text-emerald-400' : 'text-red-400'}`}>Balance: ₹{walletBalance.toFixed(2)}</p>
              </div>
            </button>
            <button onClick={() => setPayMethod('card')}
              className={`flex items-center gap-2.5 p-3.5 rounded-xl border-2 transition-all ${payMethod === 'card' ? 'border-blue-500 bg-blue-500/5' : 'border-zinc-800 hover:border-zinc-700'}`}
              data-testid="pay-card">
              <CreditCard className="w-5 h-5 text-blue-400" />
              <div className="text-left">
                <p className="text-sm font-bold">UPI / Card</p>
                <p className="text-[10px] text-zinc-500">UPI, Cards, NetBanking</p>
              </div>
            </button>
          </div>
        </div>

        {/* Summary + Book */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-400">{callType === 'video' ? 'Video Call' : 'Voice Call'} (30 min)</span>
            <span className="font-bold">₹{price.toFixed(2)}</span>
          </div>
          {selectedDate && <div className="flex justify-between text-sm">
            <span className="text-zinc-400">Date</span>
            <span className="font-medium">{selectedDate} {selectedSlot ? `at ${selectedSlot}` : ''}</span>
          </div>}
          <div className="flex justify-between text-sm">
            <span className="text-zinc-400">Payment</span>
            <span className="font-medium">{payMethod === 'wallet' ? 'Wallet' : 'UPI / Card / NetBanking'}</span>
          </div>
          <div className="border-t border-zinc-800 pt-3 flex justify-between">
            <span className="font-bold">Total</span>
            <span className="text-lg font-black">₹{price.toFixed(2)}</span>
          </div>
        </div>

        <button onClick={handleBook}
          disabled={processing || !selectedDate || !selectedSlot || (payMethod === 'wallet' && !canWalletPay)}
          className="w-full bg-white text-black font-bold rounded-full py-4 text-base disabled:opacity-30 transition-all hover:bg-zinc-100"
          data-testid="confirm-booking-btn">
          {processing ? 'Processing...' : payMethod === 'wallet' ? `Pay ₹${price} from Wallet` : `Pay ₹${price}`}
        </button>

        {payMethod === 'wallet' && !canWalletPay && (
          <p className="text-center text-xs text-red-400">Insufficient wallet balance. Top up from your profile or use card payment.</p>
        )}
      </div>
    </div>
  );
}
